import React from "react";
import {
  Gauge, Target, Sparkles, Zap, LineChart, MessageSquare, Boxes, Lock,
} from "lucide-react";

import SectionLabel from "./SectionLabel";
import { ImageSlot } from "./ImageSlot";
import { CASH_IMPACT_LABEL, OPPS_COUNT } from "./aigoMetrics";
import { useUpsellTrigger } from "./UpsellModal";

/**
 * FeatureGrid — 8 real AIGO modules.
 *
 * Exactly 7 of the 8 cards carry a labeled screenshot slot mapped to the
 * user-supplied filename list at /public/screenshots/<slotId>.png.
 * Card 8 (Autonomous Mode) is a text-only "coming to Pro" card — no image
 * expected — so the total image-slot count matches the user's canonical
 * list of 8 filenames (hero + these 7).
 *
 * Pro cards are click-through — hitting a locked card opens the Unlock-Pro
 * upsell modal via useUpsellTrigger().
 */
const FEATURES = [
  {
    slotId: "growth-score",
    icon: <Gauge className="w-4 h-4" />,
    kicker: "Free",
    title: "Growth Score · 6 pillars",
    body: "A 0–100 score across Pricing, Margin, Ad efficiency, Retention, SEO, Creative — updated live from your store.",
    aspect: "16/10",
  },
  {
    slotId: "dollar-leaks",
    icon: <Target className="w-4 h-4" />,
    kicker: "Free",
    title: "$-Leak radar",
    body: `Top slow-mover markdowns with $/mo impact and HIGH/MEDIUM confidence. All ${OPPS_COUNT} opportunities visible on Free.`,
    aspect: "16/10",
  },
  {
    slotId: "pnl",
    icon: <LineChart className="w-4 h-4" />,
    kicker: "Free",
    title: "Contribution-Margin P&L + Profit Waterfall",
    body: "Revenue is fine. Contribution margin is the truth. AIGO shows exactly where every dollar goes.",
    aspect: "16/10",
  },
  {
    slotId: "gauges",
    icon: <Gauge className="w-4 h-4" />,
    kicker: "Free",
    title: "ROI · Net Margin · KPI gauges",
    body: "Live ROI (7.32x) and Net Margin (53.7%) gauges alongside orders/day and ad-spend tiles — read at a glance.",
    aspect: "16/10",
  },
  {
    slotId: "implement-feed",
    icon: <Zap className="w-4 h-4" />,
    kicker: "Pro",
    kickerLocked: true,
    title: "1-click Implement + Adjust-Impact",
    body: "One tap ships the fix across Shopify, Google, Meta, Klaviyo. Sliders let you tune impact before you approve.",
    aspect: "16/10",
    accent: true,
  },
  {
    slotId: "operator-modules",
    icon: <Boxes className="w-4 h-4" />,
    kicker: "Pro",
    kickerLocked: true,
    title: "Operator Modules · Image, SEO, Inventory",
    body: "Image Engine, SEO Sanitizer, Inventory Guard — autonomous specialists that ship approved changes for you.",
    aspect: "16/10",
    accent: true,
  },
  {
    slotId: "copilot",
    icon: <MessageSquare className="w-4 h-4" />,
    kicker: "Pro",
    kickerLocked: true,
    title: "AI Profit Copilot",
    body: "Ask “what’s leaking today?” — Claude Sonnet 4.6 grounded in your live store data. No prompts, just answers.",
    aspect: "16/10",
    accent: true,
  },
  {
    // NO image slot — text-only Pro card.
    slotId: null,
    icon: <Sparkles className="w-4 h-4" />,
    kicker: "Pro",
    kickerLocked: true,
    title: "Autonomous Mode",
    body: "Flip from recommend-and-await-approval to auto-execute for safe, high-confidence moves. Undo any implementation.",
    accent: true,
    textOnly: true,
  },
];

export const FeatureGrid = () => {
  const { modal, open } = useUpsellTrigger();

  return (
    <section
      id="features"
      data-testid="features-section"
      className="px-6 md:px-10 py-20 md:py-24 border-t border-slate-100"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <SectionLabel>Inside the operator</SectionLabel>
          <h2 className="text-3xl md:text-5xl lg:text-6xl text-slate-900 max-w-4xl mx-auto mt-4 mb-4 tracking-tight font-bold leading-[1.05]">
            <span className="text-emerald-600">Explore</span> every opportunity free. <br className="hidden md:block" />
            <span className="text-slate-900">Put fixes to work</span> on Pro.
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Diagnosis is free · execution is paid. You’ll see the {CASH_IMPACT_LABEL} across {OPPS_COUNT} opportunities the moment you connect Shopify.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          {FEATURES.map((f) => {
            const isPro = f.kicker === "Pro";
            const testid = `feature-${f.slotId || f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

            const cardInner = (
              <>
                {/* Kicker */}
                <div className="flex items-center justify-between mb-4">
                  <div
                    className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase font-bold ${
                      isPro
                        ? "bg-slate-900 text-white"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`}
                  >
                    {f.kickerLocked && <Lock className="w-2.5 h-2.5" />}
                    {f.kicker}
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold ${
                      f.accent ? "text-emerald-700" : "text-slate-500"
                    }`}
                  >
                    {f.icon}
                  </span>
                </div>

                {/* Screenshot slot (skipped on text-only Pro card) */}
                {f.slotId && (
                  <ImageSlot
                    slotId={f.slotId}
                    label={f.title}
                    aspect={f.aspect}
                    accent={f.accent}
                  />
                )}

                {/* Copy */}
                <h3 className="mt-5 text-slate-900 text-lg md:text-xl font-bold tracking-tight leading-tight">
                  {f.title}
                </h3>
                <p className="mt-2 text-slate-600 text-sm md:text-base leading-relaxed">
                  {f.body}
                </p>

                {isPro && (
                  <div
                    data-testid={`${testid}-unlock`}
                    className="mt-4 inline-flex items-center gap-1.5 text-emerald-700 text-sm font-bold group-hover:text-emerald-800 transition"
                  >
                    <Sparkles className="w-4 h-4" />
                    Unlock on Pro →
                  </div>
                )}
              </>
            );

            const commonClasses = `group relative rounded-3xl border p-5 md:p-6 transition text-left ${
              f.accent
                ? "border-emerald-200 bg-white shadow-md shadow-emerald-500/5"
                : "border-slate-200 bg-white"
            } ${isPro ? "hover:-translate-y-0.5 hover:shadow-lg hover:shadow-emerald-500/10 cursor-pointer" : "hover:-translate-y-0.5 hover:shadow-md"}`;

            if (isPro) {
              return (
                <button
                  key={f.title}
                  data-testid={testid}
                  onClick={() => open(testid)}
                  className={`${commonClasses} w-full`}
                >
                  {cardInner}
                </button>
              );
            }
            return (
              <div key={f.title} data-testid={testid} className={commonClasses}>
                {cardInner}
              </div>
            );
          })}
        </div>
      </div>
      {modal}
    </section>
  );
};

export default FeatureGrid;
